/**
 * Manutenção no portal: os checklists de manutenção (inspeção técnica) e as
 * ordens de serviço do SIAG.
 *
 * O Checklist Completo (inspeção técnica, docs/manutencao/09) é o template
 * semeado; outros publicados pela API aparecem na mesma lista.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenanceTemplateSummaryDto, MaintenanceWorkOrderDto } from "@bi/types";
import { api } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { IconWrench } from "../../components/icons";
import { WO_STATUS, CriticalityBadge } from "../../features/maintenance/labels";
import { portalPaths } from "../../features/portal/portal-nav";
import { useCapabilities } from "../../features/maintenance/useCapabilities";
import { sortWorkQueue } from "../../features/maintenance/work-order-ui";
import { PlanStatusBadge, PLAN_STATUS } from "../../features/maintenance/preventive-ui";
import type { MaintenancePlanListDto } from "@bi/types";
import { fmtNumber } from "../../lib/format";

export function PortalMaintenancePage() {
  const { can } = useCapabilities();

  const templates = useQuery({
    queryKey: ["maintenance", "templates"],
    queryFn: () => api.get<{ items: MaintenanceTemplateSummaryDto[] }>("/maintenance/templates"),
  });
  const ordens = useQuery({
    queryKey: ["maintenance", "work-orders", "portal"],
    queryFn: () => api.get<{ rows: MaintenanceWorkOrderDto[]; total: number }>("/maintenance/work-orders?status=active&limit=50"),
  });

  const tecnicos = (templates.data?.items ?? []).filter(
    (t) => (t.kind === "maintenance" || t.kind === "inspection") && t.publishedVersion,
  );
  // Fila de trabalho: atrasada, veículo parado, prioridade, prazo.
  const abertas = sortWorkQueue(ordens.data?.rows ?? []);

  return (
    <div className="space-y-5">
      <h1 className="text-lg font-semibold text-gray-900">Manutenção</h1>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Checklist de Manutenção</h2>
        {tecnicos.length === 0 ? (
          <div className="card">
            <EmptyState
              compact
              icon={<IconWrench className="w-5 h-5" />}
              title="Nenhum checklist técnico publicado"
              description="Os itens da inspeção de manutenção serão definidos com a oficina. Publicados, eles aparecem aqui e podem ser executados pelo celular."
            />
          </div>
        ) : (
          <div className="card divide-y divide-surface-border">
            {tecnicos.map((t) => (
              <div key={t.id} className="px-4 py-3 flex items-center gap-3 min-h-[56px]">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.itemCount} itens · versão {t.publishedVersion!.version}</p>
                </div>
                {can("checklist.execute") && (
                  <Link className="btn-secondary min-h-[40px]" to={portalPaths.newChecklist("manutencao")}>Executar</Link>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3" id="ordens">
        <h2 className="text-sm font-semibold text-gray-900">Ordens de serviço abertas</h2>
        {ordens.isLoading ? (
          <p className="text-sm text-gray-500">Carregando…</p>
        ) : abertas.length === 0 ? (
          <div className="card">
            <EmptyState compact icon={<IconWrench className="w-5 h-5" />} title="Nenhuma O.S. aberta"
              description="Ordens abertas a partir de ocorrências ou do plano preventivo aparecem aqui." />
          </div>
        ) : (
          <div className="card divide-y divide-surface-border">
            {abertas.map((o) => (
              <Link key={o.id} to={portalPaths.workOrder(o.id)} className="block px-4 py-3 space-y-1 hover:bg-surface-muted">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-gray-900">
                    O.S. nº {o.number} · <span className="font-normal">{o.type === "preventive" ? "preventiva" : "corretiva"}</span>
                  </p>
                  <div className="flex items-center gap-1">
                    <CriticalityBadge value={o.priority} />
                    <span className={WO_STATUS[o.status].badge}>{WO_STATUS[o.status].label}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  <span className="font-mono text-tm-700">{o.vehiclePlate}</span>
                  {` · aberta em ${new Date(o.openedAt).toLocaleDateString("pt-BR")}`}
                  {o.dueAt ? ` · prazo ${new Date(o.dueAt).toLocaleDateString("pt-BR")}` : ""}
                </p>
                <p className="text-sm text-gray-600">{o.description}</p>
                {(o.overdue || o.vehicleStopped) && (
                  <p className="flex gap-1">
                    {o.overdue && <span className="badge-red">Atrasada</span>}
                    {o.vehicleStopped && <span className="badge-red">Veículo parado</span>}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      <Preventiva />
    </div>
  );
}

/**
 * Preventiva no celular: o que venceu e o que está vencendo, por placa. Registrar
 * e abrir O.S. ficam na tela web (Frota → Plano Preventivo), que é onde o gestor
 * decide; aqui é para a oficina saber o que fazer.
 */
function Preventiva() {
  const { data, isLoading } = useQuery({
    queryKey: ["maintenance", "preventive", "portal"],
    queryFn: () => api.get<MaintenancePlanListDto>("/maintenance/preventive"),
  });
  const urgentes = (data?.rows ?? []).filter((r) => r.status.status === "vencido" || r.status.status === "vencendo").slice(0, 20);

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-gray-900">Preventiva</h2>
      {data && (
        <div className="grid grid-cols-3 gap-2">
          {(["vencido", "vencendo", "sem_base"] as const).map((s) => (
            <div key={s} className="card p-3">
              <p className="text-xs text-gray-500">{PLAN_STATUS[s].label}</p>
              <p className={`text-xl font-semibold ${s === "vencido" && data.counts[s] ? "text-danger-600" : "text-gray-900"}`}>{fmtNumber(data.counts[s])}</p>
            </div>
          ))}
        </div>
      )}
      {isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : urgentes.length === 0 ? (
        <div className="card">
          <EmptyState compact icon={<IconWrench className="w-5 h-5" />} title="Nada vencido ou vencendo"
            description={data?.counts.sem_base ? "Há itens sem última troca confirmada. O gestor registra na tela web." : undefined} />
        </div>
      ) : (
        <div className="card divide-y divide-surface-border">
          {urgentes.map((r) => (
            <div key={r.id} className="px-4 py-3 space-y-0.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-900"><span className="font-mono">{r.plate}</span> · {r.label}</p>
                <PlanStatusBadge status={r.status.status} />
              </div>
              <p className="text-xs text-gray-500">
                {r.status.reason}
                {r.openWorkOrder ? ` · O.S. nº ${r.openWorkOrder.number}` : ""}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

